import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [showTooltip, setShowTooltip] = useState(null); // 'terms' or 'privacy' or null

  const handleLegalClick = (type) => {
    // Toggle tooltip on click (for mobile)
    setShowTooltip(showTooltip === type ? null : type);
  };

  // Close tooltip when clicking elsewhere
  const handleFooterClick = (e) => {
    if (!e.target.closest('.legal-tooltip-trigger')) {
      setShowTooltip(null);
    }
  };

  return (
    <footer 
      className="w-full"
      style={{ backgroundColor: 'var(--color-dark)', marginTop: '30px' }}
      onClick={handleFooterClick}
    >
      {/* Top Section - Logo and Links */}
      <div className="container mx-auto px-6 lg:px-8">
        <div className="py-8 sm:py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
            {theme.logo_url ? (
              <img src={theme.logo_url} alt="Logo" className="h-10 sm:h-12 w-auto" />
            ) : (
              <div className="flex items-center gap-3">
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <span 
                  className="text-lg text-white"
                  style={{ fontFamily: 'var(--font-heading)', fontWeight: 'var(--font-heading-weight, 600)' }}
                >
                  {theme.brand_name || 'RijekaBooking'}
                </span>
              </div>
            )}
          </Link>

          {/* Navigation Links */}
          <nav className="flex flex-wrap items-center justify-center gap-6 sm:gap-8">
            <Link 
              to="/" 
              className="text-white/70 hover:text-white text-sm transition-colors"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              {t('footer.home', 'Početna')}
            </Link>
            <span 
              className="text-white/70 hover:text-white/90 text-sm cursor-pointer relative group legal-tooltip-trigger"
              style={{ fontFamily: 'var(--font-body)' }}
              onClick={(e) => { e.stopPropagation(); handleLegalClick('terms'); }}
            >
              {t('footer.termsAndConditions', 'Uvjeti Korištenja')}
              {/* Desktop tooltip (hover) */}
              <span className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-white text-gray-800 text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-64 text-left z-50 hidden sm:block" style={{ whiteSpace: 'normal' }}>
                {t('footer.legalDocsTooltip', 'Politika privatnosti i Uvjeti korištenja dostupni su tijekom procesa rezervacije, nakon odabira objekta, prije plaćanja.')}
                <span className="absolute top-full right-4 border-4 border-transparent border-t-white"></span>
              </span>
              {/* Mobile tooltip (click) */}
              {showTooltip === 'terms' && (
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-white text-gray-800 text-xs rounded-lg shadow-lg w-64 text-left z-50 sm:hidden" style={{ whiteSpace: 'normal' }}>
                  {t('footer.legalDocsTooltip', 'Politika privatnosti i Uvjeti korištenja dostupni su tijekom procesa rezervacije, nakon odabira objekta, prije plaćanja.')}
                  <span className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-white"></span>
                </span>
              )}
            </span>
            <span 
              className="text-white/70 hover:text-white/90 text-sm cursor-pointer relative group legal-tooltip-trigger"
              style={{ fontFamily: 'var(--font-body)' }}
              onClick={(e) => { e.stopPropagation(); handleLegalClick('privacy'); }}
            >
              {t('footer.privacyPolicy', 'Politika Privatnosti')}
              {/* Desktop tooltip (hover) */}
              <span className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-white text-gray-800 text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-64 text-left z-50 hidden sm:block" style={{ whiteSpace: 'normal' }}>
                {t('footer.legalDocsTooltip', 'Politika privatnosti i Uvjeti korištenja dostupni su tijekom procesa rezervacije, nakon odabira objekta, prije plaćanja.')}
                <span className="absolute top-full right-4 border-4 border-transparent border-t-white"></span>
              </span>
              {/* Mobile tooltip (click) */}
              {showTooltip === 'privacy' && (
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-white text-gray-800 text-xs rounded-lg shadow-lg w-64 text-left z-50 sm:hidden" style={{ whiteSpace: 'normal' }}>
                  {t('footer.legalDocsTooltip', 'Politika privatnosti i Uvjeti korištenja dostupni su tijekom procesa rezervacije, nakon odabira objekta, prije plaćanja.')}
                  <span className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-white"></span>
                </span>
              )}
            </span>
          </nav>
        </div>

        {/* Divider Line */}
        <div className="border-t border-white/10" />

        {/* Bottom Section - Copyright and Powered By */}
        <div className="py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Copyright */}
          <p 
            className="text-white/50 text-xs sm:text-sm"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            © {currentYear} {theme.brand_name || 'RijekaBooking'}. {t('footer.allRightsReserved', 'Sva prava pridržana.')}
          </p>

          {/* Powered By */}
          <a 
            href="https://rentalweb.eu" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-white/50 hover:text-white/70 transition-colors"
          >
            <span 
              className="text-xs sm:text-sm"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Powered by
            </span>
            <img 
              src="https://customer-assets.emergentagent.com/job_b75c8c28-8fb1-49d6-b28e-c903fa61b3c2/artifacts/ny1omxuj_RENTALWEB%20LOGO%20WHITE%20w%20color.svg" 
              alt="RENTALWEB" 
              className="h-3 sm:h-3 w-auto"
            />
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
